if (!customElements.get('xs-smooth-animator')) {
    class XSSmoothAnimator extends HTMLElement {
        constructor() {
            super();
            this.ioWrap = null;      // wrap words ~200px before viewport
            this.ioInView = null;    // add .in-view once in bottom 30%
            this.wrapped = false;
            this.inViewAdded = false;
        }

        connectedCallback() {
            if (!this.ioWrap) {
                this.ioWrap = new IntersectionObserver((entries) => {
                    if (!entries[0].isIntersecting) return;
                    this.wrapAll();
                    this.ioWrap && this.ioWrap.unobserve(this);
                }, { root: null, rootMargin: '0px 0px 200px 0px', threshold: 0 });
                this.ioWrap.observe(this);
            }

            if (!this.ioInView) {
                this.ioInView = new IntersectionObserver((entries) => {
                    if (this.inViewAdded || !entries[0].isIntersecting) return;
                    this.addInView();
                    this.ioInView && this.ioInView.unobserve(this);
                }, { root: null, rootMargin: '-70% 0px 0px 0px', threshold: 0 });
                this.ioInView.observe(this);
            }

            // If already visible (e.g., editor changes), fire immediately
            requestAnimationFrame(() => this.immediateCheck());
        }

        disconnectedCallback() {
            if (this.ioWrap) { this.ioWrap.disconnect(); this.ioWrap = null; }
            if (this.ioInView) { this.ioInView.disconnect(); this.ioInView = null; }
        }

        wrapAll() {
            if (this.wrapped) return;
            const headings = this.querySelectorAll('[data-split]');
            let total = 0;
            headings.forEach(h => total += wrapWords(h));
            this.style.setProperty('--word-total', total);
            this.wrapped = true;
        }

        addInView() {
            if (this.inViewAdded) return;
            this.classList.add('in-view');
            this.inViewAdded = true;
        }

        immediateCheck() {
            const rect = this.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;

            // Wrap if within 200px of bottom (same as IO)
            if (!this.wrapped && rect.top <= vh + 200) this.wrapAll();

            // Add .in-view if:
            // 1) overlapping the bottom 30% stripe (normal trigger), OR
            // 2) visible and already above that stripe (missed trigger on load)
            const stripeTop = vh * 0.7;
            const inViewport = rect.top < vh && rect.bottom > 0;
            const overlapsStripe = rect.bottom >= stripeTop && rect.top <= vh;
            const aboveStripeButVisible = inViewport && rect.bottom < stripeTop;

            if (!this.inViewAdded && (overlapsStripe || aboveStripeButVisible)) {
                // delay a bit so the initial (pre-in-view) styles render, then animate
                const delayMs = parseInt(this.getAttribute('in-view-delay') || '80', 10);

                if (delayMs > 0) {
                    setTimeout(() => this.addInView(), delayMs);
                } else {
                    // no explicit delay: wait 2 frames to ensure paint, then add class
                    requestAnimationFrame(() => requestAnimationFrame(() => this.addInView()));
                }
            }
        }

    }

    customElements.define('xs-smooth-animator', XSSmoothAnimator);
}

function wrapWords(root) {
    if (!root || root.dataset.wordWrapped === '1') return 0;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            if (!/\S/.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
            const p = node.parentNode;
            if (!p) return NodeFilter.FILTER_REJECT;
            const tag = p.nodeName;
            if (/^(SCRIPT|STYLE|NOSCRIPT|IFRAME)$/.test(tag)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        }
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    let index = 0;
    nodes.forEach(textNode => {
        const parts = textNode.nodeValue.split(/(\s+)/); // keep whitespace
        const frag = document.createDocumentFragment();

        parts.forEach(part => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
                frag.appendChild(document.createTextNode(part));
            } else {
                const span = document.createElement('span');
                span.className = 'word';
                span.textContent = part;
                span.style.setProperty('--word-index', index++);
                frag.appendChild(span);
            }
        });

        textNode.parentNode.replaceChild(frag, textNode);
    });

    root.dataset.wordWrapped = '1';
    return index;
}

/* Shopify editor: re-check on section/block changes so animations fire in-place */
function editorRecheck(scope) {
    (scope || document).querySelectorAll('xs-smooth-animator').forEach(el => {
        el.connectedCallback && el.connectedCallback();
        el.immediateCheck && el.immediateCheck();
    });
}
document.addEventListener('shopify:section:load', e => editorRecheck(e.target));
document.addEventListener('shopify:section:select', e => editorRecheck(e.target));
document.addEventListener('shopify:block:select', e => editorRecheck(e.target));
document.addEventListener('shopify:section:reorder', e => editorRecheck(e.target));
window.addEventListener('resize', () => editorRecheck(document));